import React, { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, useToast, } from '@cargomatic/ui';
import { Plus, ChevronDown } from 'lucide-react';
import CargomaticCollapsibleCard from 'components/v2/CollapsibleCard';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import GenerateTripModal from 'components/modules/shipments/trips/GenerateTripModal';
import TripPlannerModal from 'components/modules/shipments/TripPlannerModal';
import BulkUpdateSendDOModal from 'components/modules/shipments/BulkUpdateSendDOModal';
import BulkEditModal from 'components/modules/shipments/MassUpdateModal';
import ChangeUserModal from 'components/modules/shipments/ChangeUserModal';
import CancelShipmentModal from 'components/modules/shipments/CancelShipmentModal';
import { massUpdateActions } from 'constants/MassUpdates';
import { ShipmentList } from './ShipmentList';
import { AddLegModal } from './AddLegModal';
import { AddHubModal } from './AddHubModal';
import { EditEquipmentModal } from './components/EditEquipmentModal';
// import { yardLocationAddresses, commonYardLocations } from './constants';
import { validateEquipmentNumber } from './utils';
import { getLegTypeConfig } from './constants';
import { batchUpdateLegSequence } from './api';
import { useShipmentsData } from './hooks/useShipmentsData';
import BulkBookingForm from 'components/modules/booking/BulkBookingForm';
import { useCancelLeg } from './hooks/useLegs';
import OrdersActionCreators from 'actions/OrdersActionCreators';
import { getDocumentsV2, getSignedUrl } from 'utils/DocumentsAPIUtils';
import { addHub as addHubApi, addLeg as addOrderLeg, updateLeg as updateOrderLeg, } from 'utils/OrderWebAPIUtils';
export function OrderShipments({ orderId, order, }) {
    var _a, _b, _c;
    const { shipments, setShipments, isLoading, error } = useShipmentsData(orderId);
    const { toast } = useToast();
    const cancelLegMutation = useCancelLeg();
    const queryClient = useQueryClient();
    // Modal states
    const [isAddLegModalOpen, setIsAddLegModalOpen] = useState(false);
    const [isAddHubModalOpen, setIsAddHubModalOpen] = useState(false);
    const [isEditEquipmentModalOpen, setIsEditEquipmentModalOpen] = useState(false);
    // Editing states
    const [editingShipment, setEditingShipment] = useState(null);
    const [cancelingShipment, setCancelingShipment] = useState(null);
    const [addingHubToShipment, setAddingHubToShipment] = useState(null);
    const [editingEquipment, setEditingEquipment] = useState(null);
    // Form data states
    const [legFormData, setLegFormData] = useState({
        legType: 'full_container',
        equipment: [{ assetType: 'container', equipmentNumber: '' }],
        pickupName: '',
        pickupAddress: '',
        deliverName: '',
        deliverAddress: '',
        carrier: '',
        totalWeight: '',
        weightUnit: 'lb',
    });
    const [editEquipmentFormData, setEditEquipmentFormData] = useState({
        assetType: '',
        equipmentNumber: '',
    });
    // Validation and UI states
    // Removed equipment validation warnings state (handled within modal)
    const [editEquipmentValidationWarning, setEditEquipmentValidationWarning] = useState('');
    // Selection state for bulk actions
    const [selectedShipmentIds, setSelectedShipmentIds] = useState([]);
    // Legacy modal states
    const [showGenerateTrip, setShowGenerateTrip] = useState(false);
    const [showTripPlanner, setShowTripPlanner] = useState(false);
    const [showBulkSendDO, setShowBulkSendDO] = useState(false);
    const [showBulkEdit, setShowBulkEdit] = useState(null);
    const [showBulkBookingForm, setShowBulkBookingForm] = useState(false);
    const [showChangeCarrier, setShowChangeCarrier] = useState(false);
    // Removed hub suggestions (handled within modal)
    // Use raw order equipments as-is for autocomplete
    const orderEquipment = ((order === null || order === void 0 ? void 0 : order.equipments) || []).filter((eq) => typeof (eq === null || eq === void 0 ? void 0 : eq.equipment_id) === 'string' && eq.equipment_id.trim().length > 0);
    // Shipment handlers
    const handleEditShipment = useCallback((shipment) => {
        var _a;
        // Guard: disable editing if leg type is missing
        if (!shipment.legType || shipment.legType.trim().length === 0) {
            return;
        }
        // Guard: disable editing if shipment is closed or canceled
        const status = (shipment.status || '').toLowerCase();
        if (status === 'job closed' || status === 'job canceled') {
            return;
        }
        setEditingShipment(shipment);
        const allEquipment = shipment.stops.flatMap((stop) => stop.equipmentIds);
        const uniqueEquipment = allEquipment.filter((eq, index, self) => index === self.findIndex((e) => e.id === eq.id));
        const equipmentForForm = uniqueEquipment.length > 0
            ? uniqueEquipment.map((eq) => ({
                assetType: eq.containerType,
                equipmentNumber: eq.id,
            }))
            : [{ assetType: '', equipmentNumber: '' }];
        setLegFormData({
            legType: shipment.legType,
            equipment: equipmentForForm,
            pickupName: shipment.pickup.name,
            pickupAddress: shipment.pickup.address,
            deliverName: shipment.deliver.name,
            deliverAddress: shipment.deliver.address,
            carrier: shipment.carrier,
            totalWeight: ((_a = shipment.totalWeight) === null || _a === void 0 ? void 0 : _a.toString()) || '',
            weightUnit: shipment.weightUnit,
        });
        setIsAddLegModalOpen(true);
    }, []);
    const handleDeleteShipment = useCallback((shipment) => {
        setCancelingShipment(shipment);
    }, []);
    const handleConfirmDelete = useCallback((legToCancel, reason, comment) => {
        cancelLegMutation.mutate({ legId: legToCancel._id, reason, comment }, {
            onSuccess: () => {
                setShipments((prev) => prev.filter((s) => s.shipment_id !== cancelingShipment.shipment_id));
                setCancelingShipment(null);
                // Invalidate leg summary cache so LegNumberSummary refetches renumbered legs
                queryClient.invalidateQueries({
                    queryKey: ['legNumbers'],
                });
                toast({
                    title: 'Shipment Canceled',
                    description: `Shipment ${cancelingShipment.shipment_id} has been canceled successfully.`,
                });
            },
            onError: (error) => {
                setCancelingShipment(null);
                toast({
                    title: 'Failed to cancel Shipment',
                    description: error.message ||
                        'An error occurred while deleting the shipment.',
                    variant: 'destructive',
                });
            },
        });
    }, [cancelingShipment, setShipments, toast, cancelLegMutation, queryClient]);
    const handleReorderShipments = useCallback((shipmentIds) => {
        const reorderedShipments = shipmentIds
            .map((id, idx) => ({
            shipment: shipments.find((s) => s._id === id),
            legNumber: idx + 1,
        }))
            .filter((x) => Boolean(x.shipment));
        // optimistic UI update
        setShipments(reorderedShipments.map((x) => x.shipment));
        // backend update
        const legNumbers = {};
        reorderedShipments.forEach(({ shipment, legNumber }) => {
            // API expects mapping of leg _id to its sequence number
            legNumbers[shipment._id] = legNumber;
        });
        batchUpdateLegSequence(legNumbers).catch((e) => {
            console.error('Failed to update leg sequence', e);
            // no rollback implemented here; could refetch on failure
        });
    }, [shipments, setShipments]);
    const handleGenerateDeliveryOrder = useCallback((shipmentId) => {
        OrdersActionCreators.generateDeliveryOrder(shipmentId);
    }, []);
    const handleSendDeliveryOrder = useCallback((shipmentId) => {
        setSelectedShipmentIds([shipmentId]);
        setShowBulkSendDO(true);
    }, []);
    const handleOpenDeliveryOrder = useCallback(async (shipmentId) => {
        var _a, _b, _c, _d, _e, _f;
        try {
            const docsRes = await getDocumentsV2([shipmentId], {
                types: 'delivery_order',
                options: {
                    order: { field: 'createdAt', direction: 'desc' },
                    limit: 1,
                },
            });
            if (docsRes && 'errors' in docsRes && docsRes.errors) {
                toast({
                    title: 'Failed to fetch documents',
                    description: ((_b = (_a = docsRes.errors) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.message) ||
                        'Unknown error',
                    variant: 'destructive',
                });
                return;
            }
            const docsData = docsRes.data;
            const docs = ((_c = docsData === null || docsData === void 0 ? void 0 : docsData.getDocumentsV2) === null || _c === void 0 ? void 0 : _c.documents) || [];
            if (docs.length === 0) {
                toast({
                    title: 'No Delivery Order Found',
                    description: 'No delivery order document is available for this shipment.',
                });
                return;
            }
            const key = docs[0].key;
            const signedRes = await getSignedUrl(key);
            if (signedRes && 'errors' in signedRes && signedRes.errors) {
                toast({
                    title: 'Failed to get signed URL',
                    description: ((_e = (_d = signedRes.errors) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.message) ||
                        'Unknown error',
                    variant: 'destructive',
                });
                return;
            }
            const signedData = signedRes.data;
            const url = (_f = signedData === null || signedData === void 0 ? void 0 : signedData.getSignedUrl) === null || _f === void 0 ? void 0 : _f.signedUrl;
            if (url)
                window.open(url, '_blank');
        }
        catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            toast({
                title: 'Open Delivery Order failed',
                description: message || 'Unknown error',
                variant: 'destructive',
            });
        }
    }, [toast]);
    // Equipment handlers
    const handleEditEquipment = useCallback((shipmentId, equipmentId, containerType) => {
        setEditingEquipment({ shipmentId, equipmentId, containerType });
        setEditEquipmentFormData({
            assetType: containerType,
            equipmentNumber: equipmentId,
        });
        setEditEquipmentValidationWarning('');
        setIsEditEquipmentModalOpen(true);
    }, []);
    const handleSaveEditedEquipment = useCallback(() => {
        if (!editingEquipment)
            return;
        const { shipmentId, equipmentId } = editingEquipment;
        const { assetType, equipmentNumber } = editEquipmentFormData;
        const warning = validateEquipmentNumber(equipmentNumber, assetType);
        if (warning) {
            setEditEquipmentValidationWarning(warning);
            return;
        }
        setShipments((prevShipments) => prevShipments.map((shipment) => {
            if (shipment._id === shipmentId) {
                return Object.assign(Object.assign({}, shipment), { stops: shipment.stops.map((stop) => (Object.assign(Object.assign({}, stop), { equipmentIds: stop.equipmentIds.map((eq) => eq.id === equipmentId
                            ? { id: equipmentNumber, containerType: assetType }
                            : eq) }))) });
            }
            return shipment;
        }));
        toast({
            title: 'Equipment Updated',
            description: `Equipment ${equipmentNumber} has been updated successfully.`,
        });
        setIsEditEquipmentModalOpen(false);
        setEditingEquipment(null);
        setEditEquipmentFormData({ assetType: '', equipmentNumber: '' });
        setEditEquipmentValidationWarning('');
    }, [editingEquipment, editEquipmentFormData, setShipments, toast]);
    const handleEditEquipmentFormDataChange = useCallback((field, value) => {
        setEditEquipmentFormData((prev) => (Object.assign(Object.assign({}, prev), { [field]: value })));
        if (field === 'equipmentNumber') {
            const warning = validateEquipmentNumber(value, editEquipmentFormData.assetType);
            setEditEquipmentValidationWarning(warning);
        }
        else if (field === 'assetType') {
            const warning = validateEquipmentNumber(editEquipmentFormData.equipmentNumber, value);
            setEditEquipmentValidationWarning(warning);
        }
    }, [editEquipmentFormData.assetType, editEquipmentFormData.equipmentNumber]);
    // Leg modal handlers
    const handleCloseModal = useCallback(() => {
        setIsAddLegModalOpen(false);
        setEditingShipment(null);
        setLegFormData({
            legType: 'full_container',
            equipment: [{ assetType: 'container', equipmentNumber: '' }],
            pickupName: '',
            pickupAddress: '',
            deliverName: '',
            deliverAddress: '',
            carrier: '',
            totalWeight: '',
            weightUnit: 'lb',
        });
    }, []);
    // Removed legacy equipment handlers (handled within modal)
    const handleAddLegSubmit = async (formData) => {
        var _a, _b;
        const skipEquipment = ((_a = getLegTypeConfig(formData.legType)) === null || _a === void 0 ? void 0 : _a.requiresEquipment) === false;
        const validEquipment = formData.equipment.filter((eq) => eq.assetType && eq.equipmentNumber);
        if (!skipEquipment && validEquipment.length === 0) {
            toast({
                title: 'Equipment Required',
                description: 'Please add at least one equipment entry with asset type and equipment number.',
                variant: 'destructive',
            });
            return;
        }
        const equipmentIds = validEquipment.map((eq) => ({
            id: eq.equipmentNumber,
            containerType: eq.assetType,
        }));
        const totalWeightNumber = formData.totalWeight && formData.totalWeight.trim().length > 0
            ? Number.parseFloat(formData.totalWeight)
            : null;
        // Persist via API and refresh table on success
        try {
            const shipperId = typeof order.shipper === 'string'
                ? order.shipper
                : (_b = order.shipper) === null || _b === void 0 ? void 0 : _b._id;
            // Call appropriate API based on mode
            if (editingShipment) {
                // Update existing leg; align payload with edit schema
                const pickupOriginal = editingShipment.stops.find((s) => (s.intent || '').toLowerCase() === 'pickup') || editingShipment.stops[0];
                const deliverOriginal = editingShipment.stops.find((s) => (s.intent || '').toLowerCase() === 'deliver') || editingShipment.stops[editingShipment.stops.length - 1];
                const stopsPayload = [
                    {
                        _id: pickupOriginal === null || pickupOriginal === void 0 ? void 0 : pickupOriginal._id,
                        location: {
                            name: formData.pickupName,
                            address: formData.pickupAddress,
                        },
                    },
                    {
                        _id: deliverOriginal === null || deliverOriginal === void 0 ? void 0 : deliverOriginal._id,
                        location: {
                            name: formData.deliverName,
                            address: formData.deliverAddress,
                        },
                    },
                ];
                const payload = Object.assign({ leg_type: formData.legType, 
                    // Backend expects ids
                    shipper: shipperId, total_weight: totalWeightNumber || 0, total_weight_units: formData.weightUnit, stops: stopsPayload, equipments: validEquipment.map((eq) => ({
                        equipment_id: eq.equipmentNumber,
                        _equipmentType: eq.assetType,
                    })) }, (formData.carrier ? { carrier: formData.carrier } : {}));
                await updateOrderLeg(editingShipment._id, payload);
                // Refresh the order (including equipments) after successful update
                OrdersActionCreators.getOrder(orderId);
                // Ensure the shipments list (legs) refetches after edit
                await queryClient.invalidateQueries({
                    queryKey: ['order-legs', orderId],
                });
            }
            else {
                // Create new leg (use new flattened payload expected by add endpoint)
                const payload = Object.assign(Object.assign({ leg_type: formData.legType, shipper: shipperId, weight: totalWeightNumber || 0, weight_units: formData.weightUnit, pickup_name: formData.pickupName, pickup_address: formData.pickupAddress, deliver_name: formData.deliverName, deliver_address: formData.deliverAddress, special_instructions: formData.specialInstructions || '', equipments: validEquipment.map((eq) => ({
                        equipment_id: eq.equipmentNumber,
                        _equipmentType: eq.assetType,
                    })) }, (formData.carrier ? { carrier: formData.carrier } : {})), { leg_management_version: 2 });
                await addOrderLeg(orderId, payload);
                // Refresh the order (including equipments) after successful creation
                OrdersActionCreators.getOrder(orderId);
            }
        }
        catch (e) {
            toast({
                title: 'Failed to save leg',
                description: (e === null || e === void 0 ? void 0 : e.message) ||
                    'The leg could not be saved to the server. Your local changes are kept.',
                variant: 'destructive',
            });
            return;
        }
        if (editingShipment) {
            setShipments((prev) => prev.map((s) => {
                if (s._id === editingShipment._id) {
                    return Object.assign(Object.assign({}, s), { legType: formData.legType, pickup: {
                            name: formData.pickupName,
                            address: formData.pickupAddress,
                        }, deliver: {
                            name: formData.deliverName,
                            address: formData.deliverAddress,
                        }, carrier: formData.carrier, totalWeight: totalWeightNumber || undefined, weightUnit: formData.weightUnit, stops: s.stops.map((stop, index) => index === 0
                            ? Object.assign(Object.assign({}, stop), { equipmentIds }) : stop) });
                }
                return s;
            }));
            toast({
                title: 'Leg updated',
                description: `Shipment ${editingShipment.shipment_id} has been updated successfully.`,
                variant: 'default',
            });
        }
        else {
            // New leg created: trigger a refetch so the table refreshes
            await queryClient.invalidateQueries({
                queryKey: ['order-legs', orderId],
            });
            toast({
                title: 'Leg added',
                description: 'The leg has been successfully added.',
                variant: 'default',
            });
        }
        handleCloseModal();
    };
    // Hub modal handlers
    const handleOpenAddHub = useCallback((shipment) => {
        setAddingHubToShipment(shipment);
        setIsAddHubModalOpen(true);
    }, []);
    const handleCloseAddHubModal = useCallback(() => {
        setIsAddHubModalOpen(false);
        setAddingHubToShipment(null);
        // cleared suggestions removed (handled within modal)
    }, []);
    // Removed hub suggestion handlers (handled within modal)
    // Removed legacy in-memory AddHub submit handler; now handled via API in onSubmit
    return (React.createElement("div", { "data-testid": "leg-management-order-shipments", className: "py-6 space-y-6 text-sm" },
        error && (React.createElement(Card, { className: "p-4" },
            React.createElement("div", { className: "text-center text-red-600" },
                React.createElement("p", null, error),
                React.createElement(Button, { variant: "outline", size: "sm", className: "mt-2 bg-transparent", onClick: () => window.location.reload() }, "Retry")))),
        React.createElement(CargomaticCollapsibleCard, { id: "order-shipments-legs", header: () => (React.createElement("div", { className: "flex flex-wrap items-center justify-between gap-3" },
                React.createElement("div", { className: "flex items-center gap-3" },
                    React.createElement("span", null, "Shipments"),
                    React.createElement(DropdownMenu, null,
                        React.createElement(DropdownMenuTrigger, { asChild: true },
                            React.createElement(Button, { size: "sm", variant: "default" },
                                React.createElement(Plus, { className: "h-4 w-4 mr-1", strokeWidth: 2.5 }),
                                "Add",
                                React.createElement(ChevronDown, { className: "ml-1 h-4 w-4" }))),
                        React.createElement(DropdownMenuContent, { align: "start" },
                            React.createElement(DropdownMenuItem, { onClick: (e) => {
                                    e.stopPropagation();
                                    setIsAddLegModalOpen(true);
                                } }, "Add Leg"))),
                    isLoading && (React.createElement("div", { className: "flex items-center gap-2 text-sm text-muted-foreground" },
                        React.createElement("div", { className: "h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" }),
                        "Loading shipments..."))),
                React.createElement("div", { className: "flex flex-wrap items-center gap-3" },
                    React.createElement(Button, { variant: "outline", size: "sm", onClick: (e) => {
                            e.stopPropagation();
                            setShowTripPlanner(true);
                        } }, "Plan a Trip"),
                    React.createElement(Button, { variant: "outline", size: "sm", onClick: (e) => {
                            e.stopPropagation();
                            setShowGenerateTrip(true);
                        } }, "Generate a Trip"),
                    React.createElement(Button, { variant: "outline", size: "sm", onClick: (e) => {
                            e.stopPropagation();
                            setShowBulkBookingForm(true);
                        } }, "Book Multiple Shipments"),
                    React.createElement(DropdownMenu, null,
                        React.createElement(DropdownMenuTrigger, { asChild: true },
                            React.createElement(Button, { variant: "outline", size: "sm", disabled: isLoading || selectedShipmentIds.length === 0 },
                                "Bulk Actions ",
                                React.createElement(ChevronDown, { className: "ml-2 h-4 w-4" }))),
                        React.createElement(DropdownMenuContent, { align: "end" },
                            React.createElement(DropdownMenuItem, { onClick: (e) => {
                                    e.stopPropagation();
                                    setShowBulkEdit(massUpdateActions.UPDATE_APPOINTMENT_WINDOW);
                                } }, "Update Appointment"),
                            React.createElement(DropdownMenuItem, { onClick: (e) => {
                                    e.stopPropagation();
                                    setShowBulkEdit(massUpdateActions.EDIT_DISCHARGE);
                                } }, "Update Discharge"),
                            React.createElement(DropdownMenuItem, { onClick: (e) => {
                                    e.stopPropagation();
                                    setShowBulkEdit(massUpdateActions.EDIT_LAST_FREE_DAY);
                                } }, "Update LFD"),
                            React.createElement(DropdownMenuItem, { onClick: (e) => {
                                    e.stopPropagation();
                                    setShowChangeCarrier(true);
                                } }, "Assign a Carrier"),
                            React.createElement(DropdownMenuItem, { onClick: (e) => {
                                    e.stopPropagation();
                                    setShowBulkSendDO(true);
                                } }, "Send DO")))))) },
            React.createElement(ShipmentList, { shipments: shipments, isLoading: isLoading, onReorder: handleReorderShipments, onEditShipment: handleEditShipment, onDeleteShipment: handleDeleteShipment, onOpenAddHub: handleOpenAddHub, onEditEquipment: handleEditEquipment, onSelectionChange: setSelectedShipmentIds, onGenerateDeliveryOrder: handleGenerateDeliveryOrder, onSendDeliveryOrder: handleSendDeliveryOrder, onOpenDeliveryOrder: handleOpenDeliveryOrder })),
        React.createElement(AddLegModal, { isOpen: isAddLegModalOpen, onClose: handleCloseModal, onSubmit: handleAddLegSubmit, orderEquipment: orderEquipment, editingShipment: editingShipment, defaultValues: Object.assign(Object.assign(Object.assign({}, legFormData), { equipment: legFormData.equipment.map((eq) => ({
                    assetType: eq.assetType.toLowerCase(),
                    equipmentNumber: eq.equipmentNumber,
                })), weightUnit: 'lb' }), (editingShipment
                ? {}
                : { specialInstructions: (order === null || order === void 0 ? void 0 : order.special_instructions) || '' })) }),
        React.createElement(CancelShipmentModal, { shipment: cancelingShipment, show: cancelingShipment, onCancelShipment: handleConfirmDelete, onHide: () => setCancelingShipment(null) }),
        React.createElement(AddHubModal, { isOpen: isAddHubModalOpen, onClose: handleCloseAddHubModal, defaultSpecialInstructions: (order === null || order === void 0 ? void 0 : order.special_instructions) || '', onSubmit: async (data) => {
                var _a;
                if (!addingHubToShipment)
                    return;
                try {
                    await addHubApi(addingHubToShipment._id, {
                        yard_id: data.yardId,
                        hub_type: data.hubbingType,
                        address: data.address,
                        name: data.name,
                        special_instructions: data.specialInstructions,
                    });
                    toast({
                        title: 'Added hub successfully',
                    });
                    handleCloseAddHubModal();
                    // Refresh legs list
                    await queryClient.invalidateQueries({
                        queryKey: ['order-legs', orderId],
                    });
                }
                catch (e) {
                    // Show server error if available, else fallback to Error.message, else Unknown error
                    const message = e instanceof Error
                        ? e.message
                        : ((_a = e === null || e === void 0 ? void 0 : e.entity.error) !== null && _a !== void 0 ? _a : 'Unknown error');
                    toast({
                        title: 'Add Hub failed',
                        description: message,
                        variant: 'destructive',
                    });
                }
            } }),
        React.createElement(EditEquipmentModal, { isOpen: isEditEquipmentModalOpen, onClose: () => {
                setIsEditEquipmentModalOpen(false);
                setEditingEquipment(null);
                setEditEquipmentFormData({ assetType: '', equipmentNumber: '' });
                setEditEquipmentValidationWarning('');
            }, onSave: handleSaveEditedEquipment, formData: editEquipmentFormData, onFormDataChange: handleEditEquipmentFormDataChange, validationWarning: editEquipmentValidationWarning }),
        showGenerateTrip && (React.createElement(GenerateTripModal, { show: showGenerateTrip, onClose: () => setShowGenerateTrip(false), orderId: orderId, shipments: shipments
                .filter((s) => selectedShipmentIds.includes(s._id))
                .map((s) => ({ _id: s._id, status: s.status })) })),
        showTripPlanner && (React.createElement(TripPlannerModal, { show: showTripPlanner, onClose: () => setShowTripPlanner(false), orderId: orderId })),
        showBulkSendDO && (React.createElement(BulkUpdateSendDOModal, { title: 'Send Delivery Order', send_do: true, show: showBulkSendDO, shipments: selectedShipmentIds, onHide: () => setShowBulkSendDO(false) })),
        showBulkEdit && (React.createElement(BulkEditModal, { show: true, action: showBulkEdit, onHide: () => setShowBulkEdit(null), shipments: selectedShipmentIds })),
        showChangeCarrier && (React.createElement(ChangeUserModal, { jobs: (shipments === null || shipments === void 0 ? void 0 : shipments.filter(({ _id }) => selectedShipmentIds === null || selectedShipmentIds === void 0 ? void 0 : selectedShipmentIds.includes(_id))) || [], show: showChangeCarrier, type: "carrier", onSave: (user, jobs) => {
                return ShipmentsActionCreators.updateCarrier(user, jobs).then(() => {
                    queryClient.invalidateQueries({
                        queryKey: ['order-legs', orderId],
                    });
                });
            }, onHide: () => setShowChangeCarrier(false) })),
        showBulkBookingForm && (React.createElement(BulkBookingForm, { orderId: orderId, order: order, show: showBulkBookingForm, onHide: () => setShowBulkBookingForm(false), onSaveSuccess: () => setShowBulkBookingForm(false), origin: (_a = order === null || order === void 0 ? void 0 : order.arriving) === null || _a === void 0 ? void 0 : _a[0], destination: (_b = order === null || order === void 0 ? void 0 : order.delivering) === null || _b === void 0 ? void 0 : _b[0], shipperId: typeof (order === null || order === void 0 ? void 0 : order.shipper) === 'object'
                ? (_c = order === null || order === void 0 ? void 0 : order.shipper) === null || _c === void 0 ? void 0 : _c._id
                : order === null || order === void 0 ? void 0 : order.shipper }))));
}
